import React, { FC, useEffect, useState } from 'react'
import { useAtom } from 'jotai'
import { deleteNote, getMemberNotes } from '../../../api/notes'
import { Member } from '../../../models/Member'
import { Note, mapNotes } from '../../../models/Note'
import { Overlay } from '../../ui/Sidebar'
import { DrawerHeaderMemberInfo } from '../FormElements/DrawerHeader'
import { AddNewNote } from './AddNewNote'
import { EditMemberNoteButton } from './EditMemberNoteButton'
import { EditNote } from './EditNote'
import { NoteOverviewSidebar } from './NoteOverviewSidebar'
import { profileAtom } from '../../../api/profile/profile'

type ViewStates = 'default' | 'create' | 'edit' | 'delete'

interface EditMemberNotesProps {
    member: Member
}

const EditMemberNotes: FC<EditMemberNotesProps> = ({ member }) => {
    const [notes, setNotes] = useState([])
    const [isOpen, setIsOpen] = useState(false)
    const [viewState, setViewState] = useState<ViewStates>('default')
    const [loading, setLoading] = useState(true)
    const [editNote, setEditNote] = useState<Note | undefined>(undefined)
    const [authProfile] = useAtom(profileAtom)

    const init = (): void => {
        async function fetchData(): Promise<void> {
            if (authProfile.isImitator) {
                setLoading(false)
            } else {
                try {
                    const response = await getMemberNotes(
                        member.memberId,
                        'MedlemIdent'
                    )
                    const notesMapped = mapNotes(response.Noter)

                    setNotes(notesMapped)

                    setLoading(false)
                } catch {
                    setLoading(false)
                }
            }
        }

        fetchData()
    }

    const onClickDelete = async (note: Note): Promise<void> => {
        setViewState('default')
        const response = await deleteNote(note.id)
        if (response) {
            setViewState('default')
            if (notes.length === 1) {
                setIsOpen(false)
            }
            init()
        }
    }

    useEffect(() => {
        init()
    }, [])

    if (loading) return null

    return (
        <>
            <div>
                <EditMemberNoteButton
                    onClickCreate={() => {
                        setIsOpen(true)
                        setViewState('create')
                    }}
                    onClickView={() => {
                        setIsOpen(true)
                        setViewState('default')
                    }}
                    notes={notes}
                />
            </div>

            <Overlay
                isShowing={isOpen}
                hide={() => {
                    setIsOpen(false)
                }}
            >
                <DrawerHeaderMemberInfo member={member} />

                {viewState === 'create' ? (
                    <AddNewNote
                        member={member}
                        type="member"
                        hasNotes={notes.length > 0}
                        onClickCancel={() => {
                            setViewState('default')
                            if (notes.length === 0) {
                                setIsOpen(false)
                            }
                        }}
                        onUpdated={() => {
                            setViewState('default')
                            init()
                        }}
                    />
                ) : null}

                {viewState === 'edit' && editNote ? (
                    <EditNote
                        note={editNote}
                        onCancel={() => setViewState('default')}
                        onUpdated={() => {
                            init()
                            setViewState('default')
                        }}
                    />
                ) : null}

                {viewState === 'default' ? (
                    <NoteOverviewSidebar
                        notes={notes}
                        onClickEdit={(note) => {
                            setIsOpen(true)
                            setEditNote(note)
                            setViewState('edit')
                        }}
                        onClickDelete={onClickDelete}
                        onClickCreate={() => setViewState('create')}
                    />
                ) : null}
            </Overlay>
        </>
    )
}

export { EditMemberNotes }
